import { App } from '@capacitor/app'
import { useAuthStore, usePollingStore } from '~/store'

export type AppMode = 'web' | 'mobile'

/**
 * @description Composable function to get the app mode.
 * E.g. `web` or `mobile`. Default is `mobile`.
 */
export function useAppMode() {
  const appMode = import.meta.env.VITE_APP_MODE

  const isWebMode: boolean = appMode === 'web'
  const isMobileMode: boolean = appMode !== 'web'

  /**
   * @description Get the app mode. Default is `mobile`.
   * @returns {AppMode} The app mode. E.g. `web` or `mobile`.
   */
  function getMode(): AppMode {
    return appMode === 'web' ? 'web' : 'mobile'
  }

  return {
    isWebMode,
    isMobileMode,
    getMode
  }
}

/**
 * @description Initialize app listeners for Mobile and Web.
 * Called after the app is mounted.
 */
export function useAppListeners() {
  /**
   * @description Initialise app listeners for Web
   */
  function initWebListeners() {
    const pollingStore = usePollingStore()

    App.addListener('resume', async (): Promise<void> => {
      await pollingStore.initPolling(true)
    })

    App.addListener('pause', (): void => {
      pollingStore.clearPolling()
    })
  }

  /**
   * @description Initialise app listeners for Mobile
   */
  function initMobileListeners() {
    if (isPlatform('capacitor')) {
      const pollingStore = usePollingStore()
      const authStore = useAuthStore()

      App.addListener('resume', async (): Promise<void> => {
        // First refresh the token to ensure we have a valid session
        try {
          if (authStore.getAuthSession?.idToken && authStore.getAuthSession?.refreshToken) {
            await refreshTokenAndUpdateSession(authStore.getAuthSession!.refreshToken!, authStore.updateUserAndSession)
          }
        } catch (error: any) {
          // Only sign out if there is a response and the status is not 200 (genuine auth failure)
          if (error?.response && error?.status && error.status !== 200) {
            console.warn('initMobileListeners error refreshing token, signing out', error, JSON.stringify(error))

            authStore.signOut()
          } else {
            // Ignore errors with no response or status 200 (likely navigation aborts)
            console.warn('Token refresh error ignored (no response or status 200):', error)
          }
        }
        await useTracking().initialiseTrackingAppDetails()
        await pollingStore.initPolling(true)
      })

      App.addListener('pause', (): void => {
        pollingStore.clearPolling()
      })
    }
  }

  /**
   * @description Initialize app listeners based on the app mode.
   */
  async function initAppListeners() {
    const { isMobileMode } = useAppMode()
    const pollingStore = usePollingStore()

    if (isMobileMode) {
      initMobileListeners()
    } else {
      initWebListeners()
    }

    // Start polling check on App mount, but do not call the polling callback immediately
    await pollingStore.initPolling(false)
  }

  return initAppListeners
}
